/**
 *
 * @enum {string}
 * @readonly
 */
export const Status = {
  /**
   * The player is idle and no content is loaded
   */
  IDLE: 'idle',
  /**
   * The player is currently loading content
   */
  PREPARING: 'preparing',
  /**
   * The player is playing
   */
  PLAYING: 'playing',
  /**
   * The player is paused
   */
  PAUSED: 'paused',
  /**
   * The player is buffering and does not have enough data to move forward
   */
  BUFFERING: 'buffering',
  /**
   * Playback finished
   */
  ENDED: 'ended',
  /**
   * The player reached a fatal error state. Usually the only way to recover
   * from this state is to release the player
   */
  ERROR: 'error'
};

let Track = {
  id: null,
  label: null,
  language: null,
  type: null,

  channels: -1,

  width: -1,
  height: -1,
  bitrate: -1,
};

export function createTrack(data) {
  let t = Object.assign({}, Track, data);
  if(!t.label && t.language) {
    t.label = t.language;
  }
  return t;
}

let PlayerState = {
  /**
   * The current player status
   * @type {Status}
   * @readonly
   */
  status: Status.IDLE,
  /**
   * The current playback time in seconds
   * @type {number}
   * @readonly
   */
  currentTime: 0,
  /**
   * The current playback time in seconds inside the current period
   * @type {number}
   * @readonly
   */
  periodTime: 0,
  /**
   * The current seek target. The player tries to seek to this position
   */
  targetPosition: -1,
  /**
   * The UI, for instance the seek bar, is hovering over a specific position
   */
  hoverPosition: -1,
  /**
   * The duration in seconds
   * @readonly
   * @type {number}
   */
  duration: -1,
  /**
   * The duration in seconds in the current period
   * @readonly
   * @type {number}
   */
  periodDuration: -1,
  /**
   * True if the player is playing or will start playing once enough
   * data is loaded
   * @type {boolean}
   */
  playing: false,
  /**
   * The player is loading data and currently not able to play
   */
  loading: false,
  /**
   * The current playback rate
   * @type {number}
   */
  playbackRate: 1,
  /**
   * The current volume
   * @type {number}
   */
  volume: 1,
  /**
   * True if audio is muted
   * @type {boolean}
   */
  muted: false,
  /**
   * Holds an error if raised by the player.
   *
   * @type {Object}
   */
  error: null,
  /**
   * Video renditions
   *
   * @type {Array<Track>}
   */
  videoTracks: [],
  /**
   * Audio renditions
   *
   * @type {Array<Track>}
   */
  audioTracks: [],
  /**
   * Text renditions
   *
   * @type {Array<Track>}
   */
  textTracks: [],
  /**
   * Show the player options
   */
  showOptions: false,
  /**
   * Select a specific options section to be highlighted and visible,
   * i.e. audio, text, video
   */
  optionsSection: null,
  /**
   * Fullscreen mode requested
   */
  fullscreen: false,
  /**
   * The seek range
   */
  seekRange: {start: 0, end: 0},

  /**
   * If auto-hide is disabled, this property
   * can be used to show / hide controls
   */
  showControls: true,

  activeTracks: {
    abr: true,
    video: null,
    audio: null,
    text: null,
  },

  thumb: {
    current: null,
    position: 0,
    positionRelative: 0
  },

  ad: {
    cuePoints: [],
    active: false,
    loading: false,
    playing: false,
    muted: false,
    currentTime: 0,
    duration: 0,
    positionInSequence: 1,
    sequenceLength: 1,
    title: '',
  }
};

const CastState = {
  canCast: false,
  isCasting: false,
  receiverName: '',
  startCasting: false,
  stopCasting: false,
};

/**
 * Creates a new player state with default values
 *
 * @returns {PlayerState}
 */
export let createPlayerState = () => Object.assign({}, PlayerState);

export const createCastState = () => Object.assign({}, CastState);
